import React, { createContext, useContext, useEffect } from 'react';
import { useState } from "react";
import { LoadingPage } from 'components/LoadingPage';

export type Urls = {
  page?: string
  dataroot: string
  staffroot: string
  researchroot: string
  projectroot: string
  roofimg: string
}

// The addresses the components need, from where the page is: there is no global start-up that hands them out.
export function siteUrls(lang: string): Urls {
  const base = `${location.protocol}//${location.host}/`;
  return {
    dataroot: base,
    staffroot: `${base}${lang}/team/`,
    researchroot: `${base}${lang === 'en' ? 'en/research/' : 'de/forschung/'}`,
    projectroot: `${base}${lang === 'en' ? 'en/projects/' : 'de/projekte/'}`,
    roofimg: `${base}static/images/uzl_dachfenster.jpg`,
  };
}

export const pageLanguage = (): 'de' | 'en' =>
  (document.documentElement.getAttribute('lang') ?? (location.pathname.startsWith('/en/') ? 'en' : 'de')) === 'en' ? 'en' : 'de';

// The data files of the site. An element names the ones it needs; a file is fetched once per page,
// however many elements ask for it.
const FILES = {
  institute: 'institute.json',
  projects: 'research/projects.json',
  groups: 'research/groups.json',
  fields: 'research/fields.json',
  partners: 'research/partners.json',
  users: 'users.json',
  positions: 'position.json',
  buildings: 'building.json',
  floors: 'floors.json',
  semesters: 'static/courses.json',
};
export type Dataset = keyof typeof FILES;

const requests = new Map<string, Promise<any>>();
export function loadJson(url: string): Promise<any> {
  if (!requests.has(url)) {
    requests.set(url, fetch(url).then((response) => response.json()));
  }
  return requests.get(url)!;
}

// {name: content} of the named data files, and whether they are still on their way
export function useDatasets(urls: Urls, needs: Dataset[]): [Record<string, any>, boolean] {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const key = needs.join(',');
  useEffect(() => {
    let active = true;
    Promise.all(needs.map((name) => loadJson(`${urls.dataroot}${FILES[name]}`).catch(() => null)))
      .then((contents) => active && setData(Object.fromEntries(needs.map((name, index) => [name, contents[index]]))));
    return () => { active = false; };
  }, [urls.dataroot, key]);
  return [data ?? {}, data === null];
}

type DataProps = {
  urls: Urls
  lang: 'en'|'de'
  needs: Dataset[]
  children: React.ReactNode
}

const DataContext = createContext<any>({});
export const useData = () => useContext(DataContext);
// What the components of one element share: the data files it needs, the language, and what is selected.
export function DataProvider({ urls, lang, needs, children }: DataProps) {
  const [data, loading] = useDatasets(urls, needs);
  const [type, setType] = useState<any>(null);
  const [current, setCurrent] = useState<any>(null);
  const [hover, setHover] = useState<string | null>(null);

  if (loading) {
    return <LoadingPage />;
  }
  const value = { ...data, institute: data.institute?.[0], type, current, urls, lang, setType, setCurrent, hover, setHover };
  return (
    <DataContext.Provider value={value} >
      { children }
    </DataContext.Provider>
  );
}
